import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { Drawer } from 'vaul';

import type {
  IBottomDrawerProps,
  IDrawerContentProps,
  IDrawerNesteRootProps,
  IDrawerOverlayProps,
  IDrawerPortalProps,
  IDrawerRootProps,
  IDrawerTitleProps,
  IDrawerTriggerProps,
} from './IBottomDrawerProps';

export const drawerContentVariants = cva(
  'fixed inset-x-0 bottom-0 mt-24 flex h-full flex-col z-50 rounded-t-[20px] bg-white border border-retail-gray-100 px-6 py-8',
  {
    variants: {
      mode: {
        half: 'max-h-[50%]',
        midway: 'max-h-[75%]',
        full: 'max-h-[94%] rounded-none',
        auto: 'max-h-none',
      },
    },
    defaultVariants: {
      mode: 'half',
    },
  },
);

const DrawerRoot = (props: IDrawerRootProps) => {
  const { children, onClose, ...rest } = props;
  return (
    <Drawer.Root
      onClose={() => {
        onClose?.();
      }}
      {...rest}
    >
      {children}
    </Drawer.Root>
  );
};

DrawerRoot.displayName = 'DrawerRoot';

const DrawerPortal = (props: IDrawerPortalProps) => {
  const { children, ...rest } = props;
  return <Drawer.Portal {...rest}>{children}</Drawer.Portal>;
};

DrawerPortal.displayName = 'DrawerPortal';

const DrawerOverlay = forwardRef<
  React.ElementRef<typeof Drawer.Overlay>,
  IDrawerOverlayProps
>((props, forwardedRef) => {
  const { className, ...rest } = props;
  return (
    <Drawer.Overlay
      className={twMerge('fixed inset-0 z-50 bg-black/50', className)}
      ref={forwardedRef}
      {...rest}
    />
  );
});

DrawerOverlay.displayName = 'DrawerOverlay';

const DrawerContent = forwardRef<
  React.ElementRef<typeof Drawer.Content>,
  IDrawerContentProps
>((props, forwardedRef) => {
  const { className, mode, ...rest } = props;
  return (
    <Drawer.Content
      className={twMerge(drawerContentVariants({ mode }), className)}
      ref={forwardedRef}
      {...rest}
    />
  );
});

DrawerContent.displayName = 'DrawerContent';

const DrawerTrigger = forwardRef<
  React.ElementRef<typeof Drawer.Trigger>,
  IDrawerTriggerProps
>((props, forwardedRef) => {
  return <Drawer.Trigger ref={forwardedRef} {...props} />;
});

DrawerTrigger.displayName = 'DrawerTrigger';

const DrawerNestedRoot = (props: IDrawerNesteRootProps) => {
  const { ...rest } = props;
  return <Drawer.NestedRoot {...rest} />;
};

DrawerNestedRoot.displayName = 'DrawerNestedRoot';

const DrawerTitle = (props: IDrawerTitleProps) => {
  const { ...rest } = props;
  return <Drawer.Title {...rest} />;
};

DrawerTitle.displayName = 'DrawerTitle';

const BottomDrawer = (props: IBottomDrawerProps) => {
  const {
    open = true,
    onOpenChange = () => {},
    children,
    className,
    dismissable = true,
    mode,
    onClose = () => {},
    rootProps,
    ...rest
  } = props;

  return (
    <Drawer.Root
      dismissible={dismissable}
      open={open}
      onOpenChange={onOpenChange}
      onClose={() => {
        onClose?.();
      }}
      {...rootProps}
    >
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Drawer.Content
          {...rest}
          className={twMerge(drawerContentVariants({ mode }), className)}
        >
          {children}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default BottomDrawer;
export {
  Drawer,
  DrawerContent,
  DrawerNestedRoot,
  DrawerOverlay,
  DrawerPortal,
  DrawerRoot,
  DrawerTitle,
  DrawerTrigger,
};
